import React, { FormEvent, useState } from 'react'
import cx from 'classnames'
import classes from './emailCapture.module.css'
import { EmailCaptureProps } from './interfaces/EmailCapture'
import { v4 as uuidv4 } from 'uuid'
import { ampli } from '../../ampli'

const formatPhoneNumber = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 10)

  if (digits.length <= 3) return digits
  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`
  }

  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
}

export const EmailCapture = ({
  data,
  variant,
}: {
  data: EmailCaptureProps
  variant?: 'footer'
}) => {
  const [emailSubmitted, setEmailSubmitted] = useState<boolean>(false)
  const [email, setEmail] = useState<string>('')
  const [phone, setPhone] = useState<string>('')
  const [isChecked, setIsChecked] = useState<boolean>(false)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!isFormValid) return

    let visitorId: string
    try {
      const storedId = localStorage.getItem('tia_visitor_id')
      visitorId = storedId || uuidv4()
      if (!storedId) localStorage.setItem('tia_visitor_id', visitorId)
    } catch {
      visitorId = uuidv4()
    }
    ampli.identify(visitorId, { email, phone })

    ampli.emailCaptured({
      email: email,
      phone: phone,
      location: data.emailCaptureLocation,
      consent_checked: isChecked,
    })

    setEmailSubmitted(true)
  }

  const isFormValid = email.includes('@') && phone.length === 10 && isChecked
  const usesLightText =
    variant === 'footer' ||
    data.backgroundColor === 'bcb' ||
    data.backgroundColor === 'bcblue' ||
    data.backgroundColor === 'bcp' ||
    data.backgroundColor === 'bcraspberry'

  return (
    <div
      className={cx(classes.container, data.backgroundColor, {
        cw: usesLightText,
        [classes.darkTheme]: usesLightText,
        [classes.footerVariant]: variant === 'footer',
      })}
    >
      <div className={classes.subContainer}>
        <h2 className={classes.title}>{data.title}</h2>
        {data.subTitle && (
          <span className={classes.subTitle}>{data.subTitle}</span>
        )}
        {emailSubmitted ? (
          <p className={classes.success}>Thanks for signing up.</p>
        ) : (
          <form
            className={classes.form}
            onSubmit={(e: FormEvent) => handleSubmit(e)}
          >
            <div className={classes.fieldGrid}>
              <div className={classes.field}>
                <input
                  className={cx(
                    classes.input,
                    { [classes.footerInput]: variant === 'footer' },
                    { 'footer-newsletter-input': variant === 'footer' }
                  )}
                  type="email"
                  name="email"
                  aria-label="email"
                  placeholder="Email (your-email@example.com)"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
                {email && !email.includes('@') && (
                  <p className={classes.error}>
                    Please enter a valid email address
                  </p>
                )}
              </div>
              <div className={classes.field}>
                <input
                  className={cx(
                    classes.input,
                    { [classes.footerInput]: variant === 'footer' },
                    { 'footer-newsletter-input': variant === 'footer' }
                  )}
                  type="tel"
                  name="phone"
                  aria-label="phone"
                  placeholder="Phone number (555-555-5555)"
                  required
                  inputMode="numeric"
                  maxLength={12}
                  value={formatPhoneNumber(phone)}
                  onChange={e =>
                    setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))
                  }
                />
                {phone && phone.length < 10 && (
                  <p className={classes.error}>
                    Please enter a complete 10-digit phone number
                  </p>
                )}
              </div>
            </div>

            <div className={classes.checkboxContainer}>
              <input
                type="checkbox"
                id="consent"
                name="consent"
                className={classes.checkbox}
                checked={isChecked}
                onChange={e => setIsChecked(e.target.checked)}
              />
              <label htmlFor="consent" className={classes.checkboxLabel}>
                By checking this box, you agree to receive promotional email and
                text messages from Tia related to new services, clinics,
                offerings, and other marketing messages. You can opt out of
                receiving Automated Messages at any time, including by replying
                "STOP" to an automated text message. Text HELP for more
                information.
              </label>
            </div>
            <button
              className={`${classes.submit} ${
                isFormValid ? classes.submitEnabled : classes.submitDisabled
              }`}
              type="submit"
              disabled={!isFormValid}
            >
              Sign Up
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

export default EmailCapture
